import { motion } from 'framer-motion';
import { Phone, Mail, FileCheck, Truck, Shield, TrendingUp, Globe2, MapPin } from 'lucide-react';
import { Layout, useLanguage } from '@/components/Layout';
import { translations, CONTACT_INFO, ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

export default function Home() {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    {
      icon: FileCheck,
      title: t.services.customsClearance.title,
      description: t.services.customsClearance.description
    },
    {
      icon: Truck,
      title: t.services.transitOperations.title,
      description: t.services.transitOperations.description
    },
    {
      icon: Shield,
      title: t.services.compliance.title,
      description: t.services.compliance.description
    },
    {
      icon: TrendingUp,
      title: t.services.consulting.title,
      description: t.services.consulting.description
    }
  ];

  const continents = [
    { name: t.expertise.asia, count: '5', icon: Globe2 },
    { name: t.expertise.europe, count: '6', icon: Globe2 },
    { name: t.expertise.africa, count: '7', icon: Globe2 }
  ];

  return (
    <Layout>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.TEAM_MEETING}
            alt="Kyra AVM Consulting Team - Professional customs and transit experts"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/30 to-background/80" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-4xl mx-auto text-center"
          >
            <Badge className="mb-6 px-6 py-2 text-base font-semibold bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">
              {t.hero.experience}
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-8 tracking-tight leading-tight">
              {t.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-12 leading-relaxed max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTE_PATHS.CONTACT}>
                <Button
                  size="lg"
                  className="text-lg px-10 py-7 shadow-2xl hover:shadow-primary/20 hover:scale-105 transition-all duration-300 bg-primary hover:bg-primary/90"
                >
                  {t.hero.cta}
                </Button>
              </Link>
              <Link to={ROUTE_PATHS.SERVICES}>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-10 py-7 border-2 hover:bg-accent hover:scale-105 transition-all duration-300"
                >
                  {t.services.title}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.services.title}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t.services.subtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-8 h-full hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 group">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-4">
                      {service.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to={ROUTE_PATHS.SERVICES}>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-2 hover:bg-accent hover:scale-105 transition-all duration-300"
              >
                {t.services.title}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.SERVICES_STRATEGY_2}
            alt="Global expertise"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.expertise.title}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t.expertise.subtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="p-8 text-center hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300">
                <div className="text-5xl font-bold text-primary mb-2">20+</div>
                <p className="text-lg text-muted-foreground">{t.expertise.years}</p>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="p-8 text-center hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300">
                <div className="text-5xl font-bold text-primary mb-2">18</div>
                <p className="text-lg text-muted-foreground">{t.expertise.countries}</p>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="p-8 text-center hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300">
                <div className="text-5xl font-bold text-primary mb-2">3</div>
                <p className="text-lg text-muted-foreground">Continents</p>
              </Card>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {continents.map((continent, index) => {
              const Icon = continent.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-8 hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 group">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold text-foreground">
                        {continent.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-5 h-5" />
                      <span className="text-lg">{continent.count} {t.expertise.countries.toLowerCase()}</span>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to={ROUTE_PATHS.EXPERTISE}>
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-2 hover:bg-accent hover:scale-105 transition-all duration-300"
              >
                {t.expertise.title}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-background to-background" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {t.contact.title}
            </h2>
            <p className="text-xl text-muted-foreground mb-12">
              {t.contact.getInTouch}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.a
                href={`tel:${CONTACT_INFO.phone}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex flex-col items-center p-10 bg-card rounded-2xl border border-border shadow-lg hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t.contact.phone}
                </h3>
                <p className="text-xl text-primary font-medium">
                  {CONTACT_INFO.phone}
                </p>
              </motion.a>

              <motion.a
                href={`mailto:${CONTACT_INFO.email}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex flex-col items-center p-10 bg-card rounded-2xl border border-border shadow-lg hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 transition-all duration-300 group"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Mail className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {t.contact.email}
                </h3>
                <p className="text-xl text-primary font-medium break-all">
                  {CONTACT_INFO.email}
                </p>
              </motion.a>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-12"
            >
              <Link to={ROUTE_PATHS.CONTACT}>
                <Button
                  size="lg"
                  className="text-lg px-10 py-7 shadow-2xl hover:shadow-primary/20 hover:scale-105 transition-all duration-300 bg-primary hover:bg-primary/90"
                >
                  {t.contact.title}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}